import { useEffect, useState } from "react";
import { format, parseISO, differenceInDays } from "date-fns";
import { api } from "@/lib/api";
import type { BudgetItem, BudgetCadence } from "@/lib/types";

const cadenceLabels: Record<BudgetCadence, string> = {
  monthly: "Monthly",
  quarterly: "Every 3 months",
  every_4_months: "Every 4 months",
  yearly: "Yearly",
  one_time: "One-time",
};

export function Budget() {
  const [items, setItems] = useState<BudgetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  async function load() {
    setItems(await api.budgetList());
  }
  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const income = items.filter((i) => i.kind === "income");
  const recurring = items.filter((i) => i.kind === "expense" && i.cadence !== "one_time");
  const oneOff = items
    .filter((i) => i.kind === "expense" && i.cadence === "one_time" && i.payment_date)
    .sort((a, b) => (a.payment_date! > b.payment_date! ? 1 : -1));

  const monthlyIncome = income.reduce((s, i) => s + monthlyEquiv(i), 0);
  const monthlyExpense = recurring.reduce((s, i) => s + monthlyEquiv(i), 0);
  const surplus = monthlyIncome - monthlyExpense;

  const today = new Date();
  const next90 = oneOff.filter((i) => {
    const d = differenceInDays(parseISO(i.payment_date!), today);
    return d >= -1 && d <= 365;
  });

  const totalNext90 = next90.reduce((s, i) => s + i.amount, 0);

  if (loading) return <p className="text-muted">Loading...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Budget</h1>
          <p className="text-sm text-muted">Monthly cashflow + upcoming one-offs.</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className={showAdd ? "btn-ghost" : "btn-primary"}>
          {showAdd ? "Close" : "+ Add item"}
        </button>
      </div>

      {showAdd && (
        <AddForm onAdded={async () => { await load(); setShowAdd(false); }} />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Monthly income" value={inr(monthlyIncome)} color="accent" />
        <Kpi label="Monthly expenses" value={inr(monthlyExpense)} color="danger" />
        <Kpi label="Monthly surplus" value={inr(surplus)} color={surplus >= 0 ? "accent" : "danger"} />
        <Kpi label="One-offs next 12mo" value={inr(totalNext90)} color="warn" sub={`${next90.length} items`} />
      </div>

      <Section
        title="Income"
        items={income}
        onChanged={load}
        showAmortized={false}
      />
      <Section
        title="Recurring expenses"
        items={recurring}
        onChanged={load}
        showAmortized={true}
      />

      <div className="card">
        <h2 className="font-semibold mb-3">Upcoming one-offs (next 12 months)</h2>
        {oneOff.length === 0 ? (
          <p className="text-sm text-muted">None. Add items as they come up.</p>
        ) : (
          <ul className="space-y-2">
            {oneOff.map((i) => {
              const days = differenceInDays(parseISO(i.payment_date!), today);
              const past = days < 0;
              return (
                <li key={i.id} className="flex items-center justify-between border-b border-border pb-2 gap-3">
                  <div className="min-w-0">
                    <div className={`text-sm ${past ? "text-muted line-through" : ""}`}>{i.label}</div>
                    {i.notes && <div className="text-xs text-muted truncate">{i.notes}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-sm">{inr(i.amount)}</div>
                    <div className="text-xs text-muted">
                      {format(parseISO(i.payment_date!), "d MMM yyyy")}
                      {!past && ` · ${days}d`}
                    </div>
                  </div>
                  <DeleteButton id={i.id} onDeleted={load} />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function Section({
  title, items, onChanged, showAmortized,
}: { title: string; items: BudgetItem[]; onChanged: () => void; showAmortized: boolean }) {
  return (
    <div className="card">
      <h2 className="font-semibold mb-3">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-muted">No items yet.</p>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((i) => (
            <ItemRow key={i.id} item={i} showAmortized={showAmortized} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ItemRow({
  item, showAmortized, onChanged,
}: { item: BudgetItem; showAmortized: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(item.label);
  const [amount, setAmount] = useState(String(item.amount));
  const [cadence, setCadence] = useState<BudgetCadence>(item.cadence);
  const [paymentDate, setPaymentDate] = useState(item.payment_date ?? "");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.budgetUpdate(item.id, {
        label, amount: Number(amount), cadence,
        payment_date: paymentDate || null,
      });
      onChanged(); setEditing(false);
    } catch (err) { alert(String(err)); } finally { setSaving(false); }
  }

  if (editing) {
    return (
      <li className="py-3 space-y-2">
        <input className="field" value={label} onChange={(e) => setLabel(e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <input type="number" inputMode="numeric" className="field"
            value={amount} onChange={(e) => setAmount(e.target.value)} />
          <select className="field" value={cadence} onChange={(e) => setCadence(e.target.value as BudgetCadence)}>
            {(Object.keys(cadenceLabels) as BudgetCadence[]).map((k) => (
              <option key={k} value={k}>{cadenceLabels[k]}</option>
            ))}
          </select>
        </div>
        {(cadence === "yearly" || cadence === "quarterly" || cadence === "every_4_months" || cadence === "one_time") && (
          <input type="date" className="field" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
        )}
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} className="btn-primary flex-1">
            {saving ? "..." : "Save"}
          </button>
          <button onClick={() => setEditing(false)} className="btn-ghost">Cancel</button>
        </div>
      </li>
    );
  }

  return (
    <li className="py-2.5 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm">{item.label}</div>
        <div className="text-xs text-muted">
          {cadenceLabels[item.cadence]}
          {item.payment_date && ` · ${format(parseISO(item.payment_date), "d MMM yyyy")}`}
          {item.notes && ` · ${item.notes}`}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm">{inr(item.amount)}</div>
        {showAmortized && item.cadence !== "monthly" && (
          <div className="text-xs text-muted">= {inr(monthlyEquiv(item))}/mo</div>
        )}
      </div>
      <button onClick={() => setEditing(true)} className="text-xs text-muted hover:text-white">edit</button>
      <DeleteButton id={item.id} onDeleted={onChanged} />
    </li>
  );
}

function DeleteButton({ id, onDeleted }: { id: string; onDeleted: () => void }) {
  return (
    <button
      onClick={async () => {
        if (!confirm("Delete this item?")) return;
        await api.budgetDelete(id);
        onDeleted();
      }}
      className="text-xs text-muted hover:text-danger"
    >
      ×
    </button>
  );
}

function AddForm({ onAdded }: { onAdded: () => void }) {
  const [kind, setKind] = useState<"income" | "expense">("expense");
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [cadence, setCadence] = useState<BudgetCadence>("monthly");
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.budgetCreate({
        label, kind, cadence,
        amount: Number(amount),
        payment_date: paymentDate || null,
        notes: notes || null,
      });
      onAdded();
    } catch (err) { alert(String(err)); } finally { setSaving(false); }
  }

  const dateRequired = cadence !== "monthly";

  return (
    <form onSubmit={submit} className="card space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Kind</label>
          <select className="field" value={kind} onChange={(e) => setKind(e.target.value as "income" | "expense")}>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
        </div>
        <div>
          <label className="label">Cadence</label>
          <select className="field" value={cadence} onChange={(e) => setCadence(e.target.value as BudgetCadence)}>
            {(Object.keys(cadenceLabels) as BudgetCadence[]).map((k) => (
              <option key={k} value={k}>{cadenceLabels[k]}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="label">Label</label>
        <input className="field" value={label} onChange={(e) => setLabel(e.target.value)} required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Amount (₹)</label>
          <input type="number" inputMode="numeric" className="field"
            value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </div>
        <div>
          <label className="label">{cadence === "one_time" ? "Date" : "Next payment date"}</label>
          <input type="date" className="field" value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)} required={dateRequired} />
        </div>
      </div>
      <div>
        <label className="label">Notes (optional)</label>
        <input className="field" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <button className="btn-primary w-full" disabled={saving}>{saving ? "Saving..." : "Add"}</button>
    </form>
  );
}

function Kpi({
  label, value, sub, color,
}: { label: string; value: string; sub?: string; color: "accent" | "danger" | "warn" }) {
  const cls = color === "danger" ? "text-danger" : color === "warn" ? "text-warn" : "text-accent";
  return (
    <div className="card">
      <div className="text-xs text-muted uppercase tracking-wider">{label}</div>
      <div className={`text-xl font-mono font-semibold ${cls}`}>{value}</div>
      {sub && <div className="text-xs text-muted">{sub}</div>}
    </div>
  );
}

function monthlyEquiv(i: BudgetItem): number {
  switch (i.cadence) {
    case "monthly": return i.amount;
    case "quarterly": return i.amount / 3;
    case "every_4_months": return i.amount / 4;
    case "yearly": return i.amount / 12;
    case "one_time": return 0;
  }
}

function inr(n: number): string {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}k`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}
